import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Briefcase,
  Building2,
  Camera, CheckCircle2, ChevronRight,
  Cpu, Database,
  Download,
  Edit3,
  FileText, Globe,
  GraduationCap,
  Lock,
  Mail, MapPin,
  Network,
  Phone,
  RefreshCw,
  Settings,
  Shield,
  Star, Terminal,
  User, Users, Zap
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../config/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

// ─── HELPERS ────────────────────────────────────────────────────────────────

const formatDate = (ts) => {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '—' : format(d, 'dd MMM yyyy', { locale: fr });
};

const getRoleConfig = (role) => {
  const configs = {
    admin: {
      label: 'ADMIN_NODE',
      color: 'text-red-400',
      border: 'border-red-500/30',
      bg: 'bg-red-500/5',
      accent: '#ef4444',
      icon: <Shield size={14} />,
      badge: 'bg-red-500',
    },
    'super-admin': {
      label: 'SUPER_ROOT',
      color: 'text-red-400',
      border: 'border-red-500/30',
      bg: 'bg-red-500/5',
      accent: '#ef4444',
      icon: <Shield size={14} />,
      badge: 'bg-red-600',
    },
    entreprise: {
      label: 'CORP_ENTITY',
      color: 'text-sky-400',
      border: 'border-sky-500/30',
      bg: 'bg-sky-500/5',
      accent: '#38bdf8',
      icon: <Briefcase size={14} />,
      badge: 'bg-sky-500',
    },
    établissement: {
      label: 'EDU_INSTIT',
      color: 'text-emerald-400',
      border: 'border-emerald-500/30',
      bg: 'bg-emerald-500/5',
      accent: '#34d399',
      icon: <GraduationCap size={14} />,
      badge: 'bg-emerald-500',
    },
    collectivité: {
      label: 'COLL_ORG',
      color: 'text-purple-400',
      border: 'border-purple-500/30',
      bg: 'bg-purple-500/5',
      accent: '#a78bfa',
      icon: <Globe size={14} />,
      badge: 'bg-purple-500',
    },
    particulier: {
      label: 'AGENT_NODE',
      color: 'text-orange-400',
      border: 'border-orange-500/30',
      bg: 'bg-orange-500/5',
      accent: '#f97316',
      icon: <User size={14} />,
      badge: 'bg-orange-500',
    },
    default: {
      label: 'USER_NODE',
      color: 'text-orange-400',
      border: 'border-orange-500/30',
      bg: 'bg-orange-500/5',
      accent: '#f97316',
      icon: <User size={14} />,
      badge: 'bg-orange-500',
    },
  };
  return configs[role] || configs.default;
};

const getRank = (score) => {
  if (score >= 80) return 'Expert';
  if (score >= 60) return 'Avancé';
  if (score >= 40) return 'Intermédiaire';
  if (score >= 20) return 'Confirmé';
  return 'Apprenti';
};

// ─── SUB-COMPONENTS ─────────────────────────────────────────────────────────

const ScanLine = () => (
  <motion.div
    animate={{ top: ['-5%', '105%'] }}
    transition={{ duration: 3.5, repeat: Infinity, ease: 'linear' }}
    className="absolute left-0 right-0 h-[1px] bg-orange-500/30 z-20 pointer-events-none shadow-[0_0_10px_rgba(249,115,22,0.5)]"
  />
);

const GridPattern = () => (
  <div
    className="absolute inset-0 opacity-[0.025] pointer-events-none"
    style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '18px 18px' }}
  />
);

const SectionHeader = ({ icon, label, sub, darkMode }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <span className="text-orange-500/60">{icon}</span>
      <span className={`text-[10px] font-mono font-bold uppercase tracking-[0.3em] ${darkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
        {label}
      </span>
    </div>
    {sub && (
      <span className="text-[8px] font-mono text-zinc-600 uppercase tracking-widest">{sub}</span>
    )}
  </div>
);

const DataRow = ({ icon, label, value, editable, onEdit, darkMode, accent = 'text-orange-400' }) => (
  <div className={`group flex items-center gap-3 py-2.5 border-b transition-colors ${darkMode ? 'border-white/5 hover:bg-white/[0.02]' : 'border-zinc-100 hover:bg-zinc-50'}`}>
    <span className="text-zinc-600 flex-shrink-0">{icon}</span>
    <div className="flex-1 min-w-0">
      <div className="text-[8px] font-mono text-zinc-600 uppercase tracking-widest leading-none mb-0.5">{label}</div>
      <div className={`text-[11px] font-mono font-bold truncate ${darkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
        {value || <span className="text-zinc-600 italic">Non défini</span>}
      </div>
    </div>
    {editable && (
      <button
        onClick={onEdit}
        className={`opacity-0 group-hover:opacity-100 p-1.5 rounded border transition-all ${darkMode ? 'border-white/10 hover:border-orange-500/50 hover:bg-orange-500/10 text-zinc-500 hover:text-orange-400' : 'border-zinc-200 hover:border-orange-400 text-zinc-400 hover:text-orange-500'}`}
      >
        <Edit3 size={10} />
      </button>
    )}
  </div>
);

const StatChip = ({ label, value, color = 'orange', darkMode }) => {
  const colors = {
    orange: 'text-orange-400',
    emerald: 'text-emerald-400',
    sky: 'text-sky-400',
    purple: 'text-purple-400',
    red: 'text-red-400',
  };
  return (
    <div className={`relative p-4 overflow-hidden border transition-colors ${darkMode ? 'border-white/5 bg-black/20 hover:bg-white/[0.03]' : 'border-zinc-200 bg-white hover:bg-zinc-50'}`}>
      <div className="text-[8px] font-mono text-zinc-600 uppercase tracking-widest mb-2">{label}</div>
      <div className={`text-2xl font-black tracking-tighter ${darkMode ? 'text-white' : 'text-zinc-900'}`}>{value ?? '0'}</div>
      <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${colors[color]} opacity-30`}
        style={{ background: `linear-gradient(90deg, transparent, currentColor, transparent)` }}
      />
    </div>
  );
};

// ─── ROLE-SPECIFIC PANELS ───────────────────────────────────────────────────

const ParticulierPanel = ({ userData, editCount, MAX_EDITS, onEdit, darkMode }) => (
  <div className="space-y-4">
    <DataRow icon={<Zap size={12} />} label="Pesas_Reserve" value={`${userData.pesas} PESA`} darkMode={darkMode} accent="text-orange-400" />
    <DataRow icon={<Shield size={12} />} label="Score_Confiance" value={`${userData.scoreConfiance}%`} darkMode={darkMode} />
    <DataRow icon={<Activity size={12} />} label="Niveau_Global" value={getRank(userData.scoreConfiance)} darkMode={darkMode} />
    <DataRow icon={<Terminal size={12} />} label="Compétences" value={userData.competences} editable={editCount < MAX_EDITS} onEdit={() => onEdit('competences')} darkMode={darkMode} />
    <DataRow icon={<Database size={12} />} label="Besoins" value={userData.besoins} editable={editCount < MAX_EDITS} onEdit={() => onEdit('besoins')} darkMode={darkMode} />
    <DataRow icon={<BarChart3 size={12} />} label="Attentes_Financières" value={userData.attentesFinancieres} editable={editCount < MAX_EDITS} onEdit={() => onEdit('attentesFinancieres')} darkMode={darkMode} />
    <DataRow icon={<Star size={12} />} label="Style_Apprentissage" value={userData.learningStyle} darkMode={darkMode} />
  </div>
);

const EntreprisePanel = ({ userData, editCount, MAX_EDITS, onEdit, darkMode }) => (
  <div className="space-y-4">
    <DataRow icon={<Building2 size={12} />} label="Organisation" value={userData.organizationName} editable={editCount < MAX_EDITS} onEdit={() => onEdit('organizationName')} darkMode={darkMode} />
    <DataRow icon={<Shield size={12} />} label="Score_Confiance" value={`${userData.scoreConfiance}%`} darkMode={darkMode} />
    <DataRow icon={<Zap size={12} />} label="Pesas_Reserve" value={`${userData.pesas} PESA`} darkMode={darkMode} />
    <DataRow icon={<BarChart3 size={12} />} label="Abonnement" value={userData.subscription?.plan?.toUpperCase() || 'FREEMIUM'} darkMode={darkMode} />
    <DataRow icon={<Network size={12} />} label="Secteur_Activité" value={userData.sector} editable={editCount < MAX_EDITS} onEdit={() => onEdit('sector')} darkMode={darkMode} />
    <DataRow icon={<FileText size={12} />} label="Description" value={userData.description} editable={editCount < MAX_EDITS} onEdit={() => onEdit('description')} darkMode={darkMode} />
    <DataRow icon={<Globe size={12} />} label="Site_Web" value={userData.website} editable={editCount < MAX_EDITS} onEdit={() => onEdit('website')} darkMode={darkMode} />
  </div>
);

const EtablissementPanel = ({ userData, editCount, MAX_EDITS, onEdit, darkMode }) => (
  <div className="space-y-4">
    <DataRow icon={<GraduationCap size={12} />} label="Établissement" value={userData.organizationName} editable={editCount < MAX_EDITS} onEdit={() => onEdit('organizationName')} darkMode={darkMode} />
    <DataRow icon={<MapPin size={12} />} label="Localisation" value={userData.location} editable={editCount < MAX_EDITS} onEdit={() => onEdit('location')} darkMode={darkMode} />
    <DataRow icon={<Shield size={12} />} label="Score_Confiance" value={`${userData.scoreConfiance}%`} darkMode={darkMode} />
    <DataRow icon={<Zap size={12} />} label="Pesas_Reserve" value={`${userData.pesas} PESA`} darkMode={darkMode} />
    <DataRow icon={<FileText size={12} />} label="Type_Établissement" value={userData.etablissementType} editable={editCount < MAX_EDITS} onEdit={() => onEdit('etablissementType')} darkMode={darkMode} />
    <DataRow icon={<Users size={12} />} label="Capacité_Apprenants" value={userData.capacity} editable={editCount < MAX_EDITS} onEdit={() => onEdit('capacity')} darkMode={darkMode} />
    <DataRow icon={<BarChart3 size={12} />} label="Abonnement" value={userData.subscription?.plan?.toUpperCase() || 'FREEMIUM'} darkMode={darkMode} />
  </div>
);

const CollectivitePanel = ({ userData, editCount, MAX_EDITS, onEdit, darkMode }) => (
  <div className="space-y-4">
    <DataRow icon={<Globe size={12} />} label="Collectivité" value={userData.organizationName} editable={editCount < MAX_EDITS} onEdit={() => onEdit('organizationName')} darkMode={darkMode} />
    <DataRow icon={<MapPin size={12} />} label="Territoire" value={userData.location} editable={editCount < MAX_EDITS} onEdit={() => onEdit('location')} darkMode={darkMode} />
    <DataRow icon={<Shield size={12} />} label="Score_Confiance" value={`${userData.scoreConfiance}%`} darkMode={darkMode} />
    <DataRow icon={<Zap size={12} />} label="Pesas_Reserve" value={`${userData.pesas} PESA`} darkMode={darkMode} />
    <DataRow icon={<Network size={12} />} label="Type_Collectivité" value={userData.collectiviteType} editable={editCount < MAX_EDITS} onEdit={() => onEdit('collectiviteType')} darkMode={darkMode} />
    <DataRow icon={<Users size={12} />} label="Population_Cible" value={userData.population} editable={editCount < MAX_EDITS} onEdit={() => onEdit('population')} darkMode={darkMode} />
    <DataRow icon={<BarChart3 size={12} />} label="Abonnement" value={userData.subscription?.plan?.toUpperCase() || 'FREEMIUM'} darkMode={darkMode} />
  </div>
);

const AdminPanel = ({ userData, darkMode }) => (
  <div className="space-y-4">
    <DataRow icon={<Shield size={12} />} label="Clearance_Level" value="SYSTEM_OPERATOR" darkMode={darkMode} accent="text-red-400" />
    <DataRow icon={<Cpu size={12} />} label="Tenant_ID" value={userData.tenantId} darkMode={darkMode} />
    <DataRow icon={<Database size={12} />} label="Node_Status" value={userData.status?.toUpperCase() || 'ACTIVE'} darkMode={darkMode} />
    <DataRow icon={<Activity size={12} />} label="Last_Sync" value={formatDate(userData.lastSync)} darkMode={darkMode} />
    <DataRow icon={<RefreshCw size={12} />} label="Last_Login" value={formatDate(userData.lastLogin)} darkMode={darkMode} />
    <DataRow icon={<Terminal size={12} />} label="Pesa_Role" value={userData.pesaRole || '—'} darkMode={darkMode} />
    <DataRow icon={<Zap size={12} />} label="Pesas_Reserve" value={`${userData.pesas} PESA`} darkMode={darkMode} />
  </div>
);

// ─── PROGRESS RING ───────────────────────────────────────────────────────────

const ProgressRing = ({ value, max = 500, accent = '#f97316', label, sub, darkMode }) => {
  const pct = Math.min((value / max) * 100, 100);
  const R = 40;
  const circ = 2 * Math.PI * R;
  const dash = circ - (pct / 100) * circ;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={R} fill="none" strokeWidth="6" stroke={darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'} />
          <motion.circle
            cx="50" cy="50" r={R}
            fill="none" strokeWidth="6"
            stroke={accent}
            strokeLinecap="round"
            strokeDasharray={circ}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: dash }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            style={{ filter: `drop-shadow(0 0 6px ${accent}80)` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-lg font-black tracking-tighter ${darkMode ? 'text-white' : 'text-zinc-900'}`}>{value}</span>
          <span className="text-[7px] font-mono text-zinc-600 uppercase">{label}</span>
        </div>
      </div>
      <div className="text-[8px] font-mono text-zinc-600 text-center uppercase tracking-widest">{sub}</div>
    </div>
  );
};

// ─── PHOTO AVATAR ────────────────────────────────────────────────────────────

const Avatar = ({ userData, onUpload, roleConfig, darkMode }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <div className="relative flex-shrink-0">
      <div className={`relative w-24 h-24 rounded-sm border-2 overflow-hidden ${roleConfig.border}`}
        style={{ boxShadow: `0 0 20px ${roleConfig.accent}30` }}
      >
        {userData?.photoUrl ? (
          <img
            src={userData.photoUrl}
            alt={userData.nom}
            className="w-full h-full object-cover"
            onLoad={() => setImgLoaded(true)}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
            <User size={36} className="text-zinc-500" />
          </div>
        )}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,1) 2px, rgba(0,0,0,1) 4px)' }}
        />
      </div>
      <label className={`absolute -bottom-2 -right-2 p-1.5 rounded-sm cursor-pointer border transition-all ${darkMode ? 'bg-zinc-900 border-orange-500/40 hover:bg-orange-500/20 text-orange-400' : 'bg-white border-orange-400/60 hover:bg-orange-50 text-orange-500'}`}
        style={{ boxShadow: `0 0 10px ${roleConfig.accent}30` }}
      >
        <Camera size={12} />
        <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
      </label>
      <div className={`absolute -top-2 -left-2 px-1.5 py-0.5 text-[7px] font-mono font-bold uppercase border flex items-center gap-1 ${roleConfig.color} ${roleConfig.border} ${darkMode ? 'bg-zinc-900' : 'bg-white'}`}>
        {roleConfig.icon}
        {roleConfig.label}
      </div>
    </div>
  );
};

// ─── CONNECTIONS SECTION ─────────────────────────────────────────────────────

const ConnectionsSection = ({ connexions, darkMode }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5 }}
    className={`relative overflow-hidden border ${darkMode ? 'border-white/5 bg-black/20' : 'border-zinc-200 bg-white'}`}
  >
    <GridPattern />
    <div className="relative z-10 p-6">
      <SectionHeader icon={<Network size={14} />} label="Réseau_Connexions" sub={`${connexions?.length || 0} nodes`} darkMode={darkMode} />
      {connexions && connexions.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {connexions.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * i }}
              className={`group flex items-center gap-3 p-3 border-l-2 transition-all cursor-pointer ${darkMode ? 'bg-black/20 border-orange-500/30 hover:bg-orange-500/5' : 'bg-zinc-50 border-orange-400/40 hover:bg-orange-50/50'}`}
            >
              <div className={`w-8 h-8 flex items-center justify-center border ${darkMode ? 'bg-zinc-800 border-white/10' : 'bg-white border-zinc-200'}`}>
                <User size={14} className="text-zinc-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[11px] font-bold uppercase tracking-tight truncate ${darkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>{c.nom}</div>
                <div className="text-[8px] font-mono text-zinc-600 uppercase truncate">{c.statut}</div>
              </div>
              <ChevronRight size={12} className="text-zinc-700 group-hover:text-orange-500 transition-colors flex-shrink-0" />
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="py-8 text-center">
          <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest italic">No_Connections_Found</div>
          <div className="text-[8px] font-mono text-zinc-700 mt-1">Awaiting_network_initialization...</div>
        </div>
      )}
    </div>
  </motion.section>
);

// ─── BOTTOM ACTION BAR ───────────────────────────────────────────────────────

const ActionBar = ({ onEdit, darkMode, navigate }) => (
  <motion.div
    initial={{ y: 20, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ delay: 0.6 }}
    className={`fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 p-1 rounded-sm border z-50 backdrop-blur-xl shadow-2xl ${darkMode ? 'bg-zinc-900/90 border-white/5' : 'bg-white/90 border-zinc-200'}`}
    style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
  >
    {[
      { icon: <Edit3 size={16} />, color: 'text-blue-400', bg: 'hover:bg-blue-500/10', action: () => navigate('/pysa'), tip: 'Éditer' },
      { icon: <Lock size={16} />, color: 'text-emerald-400', bg: 'hover:bg-emerald-500/10', action: () => {}, tip: 'Sécurité' },
      { icon: <Download size={16} />, color: 'text-yellow-400', bg: 'hover:bg-yellow-500/10', action: () => {}, tip: 'Export' },
      { icon: <Settings size={16} />, color: 'text-zinc-400', bg: 'hover:bg-white/5', action: () => {}, tip: 'Paramètres' },
    ].map((btn, i) => (
      <button
        key={i}
        onClick={btn.action}
        title={btn.tip}
        className={`relative p-3 rounded-sm transition-all group ${btn.color} ${btn.bg}`}
      >
        {btn.icon}
        <span className={`absolute -top-7 left-1/2 -translate-x-1/2 text-[8px] font-mono uppercase whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity px-1.5 py-0.5 ${darkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-900 text-zinc-100'}`}>
          {btn.tip}
        </span>
      </button>
    ))}
  </motion.div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

const UserProfile = () => {
  const { isAuthenticated, user, setProfile, refreshToken } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editCount, setEditCount] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);
  const MAX_EDITS = 3;

  const fetchUserData = useCallback(async () => {
    if (!isAuthenticated) { setError('Non authentifié'); setLoading(false); return; }
    try {
      const res = await api.get('/users/me');
      setUserData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          await refreshToken();
          const retry = await api.get('/users/me');
          setUserData(retry.data);
        } catch { setError('Session expirée.'); }
      } else if (err.response?.status === 429) {
        setError('Trop de requêtes. Réessayez plus tard.');
      } else {
        setError('Erreur de récupération des données.');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, refreshToken]);

  useEffect(() => {
    if (!hasFetched) { fetchUserData(); setHasFetched(true); }
  }, [fetchUserData, hasFetched]);

  const handleEditField = async (field) => {
    if (editCount >= MAX_EDITS) { toast.warn('Limite de modifications atteinte.'); return; }
    const newValue = prompt(`Modifier ${field}:`, userData[field]);
    if (!newValue) return;
    try {
      const res = await api.put('/users/me/profile-update', { [field]: newValue });
      if (res.status === 200) {
        setUserData(prev => ({ ...prev, [field]: newValue }));
        setProfile(prev => ({ ...prev, [field]: newValue }));
        setEditCount(prev => prev + 1);
        toast.success(`${field} mis à jour.`);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          await refreshToken();
          await api.put('/users/me/profile-update', { [field]: newValue });
          setUserData(prev => ({ ...prev, [field]: newValue }));
          setEditCount(prev => prev + 1);
        } catch { toast.error('Session expirée.'); }
      } else {
        toast.error(`Erreur: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const handleUploadPhoto = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Image requise.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB.'); return; }
    const fd = new FormData();
    fd.append('photoProfile', file);
    try {
      const res = await api.post('/users/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.status === 200 && res.data.photoUrl) {
        setUserData(prev => ({ ...prev, photoUrl: res.data.photoUrl }));
        setProfile(prev => ({ ...prev, photoUrl: res.data.photoUrl }));
        toast.success('Photo mise à jour.');
      }
    } catch (err) {
      toast.error(`Erreur upload: ${err.response?.data?.message || err.message}`);
    }
  };

  // ── LOADING ──
  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${darkMode ? 'bg-[#020617]' : 'bg-[#f4f7f9]'}`}>
        <div className="flex flex-col items-center gap-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <Cpu size={32} className="text-orange-500" />
          </motion.div>
          <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-[0.4em] animate-pulse">
            Loading_Profile_Data...
          </div>
        </div>
      </div>
    );
  }

  // ── ERROR ──
  if (error) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${darkMode ? 'bg-[#020617]' : 'bg-[#f4f7f9]'}`}>
        <div className="flex flex-col items-center gap-3 text-center p-8">
          <AlertTriangle size={32} className="text-red-400" />
          <div className="text-[10px] font-mono text-red-400 uppercase tracking-widest">{error}</div>
          <button onClick={fetchUserData} className="mt-2 px-4 py-2 text-[10px] font-mono border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition-all uppercase tracking-widest">
            Retry_Connection
          </button>
        </div>
      </div>
    );
  }

  if (!userData) return null;

  const role = userData.role || 'particulier';
  const roleConfig = getRoleConfig(role);
  const isOrg = ['admin', 'super-admin', 'entreprise', 'établissement', 'collectivité'].includes(role);
  const maxPesas = 500;
  const pct = Math.min((userData.pesas / maxPesas) * 100, 100);

  const renderRolePanel = () => {
    switch (role) {
      case 'entreprise': return <EntreprisePanel userData={userData} editCount={editCount} MAX_EDITS={MAX_EDITS} onEdit={handleEditField} darkMode={darkMode} />;
      case 'établissement': return <EtablissementPanel userData={userData} editCount={editCount} MAX_EDITS={MAX_EDITS} onEdit={handleEditField} darkMode={darkMode} />;
      case 'collectivité': return <CollectivitePanel userData={userData} editCount={editCount} MAX_EDITS={MAX_EDITS} onEdit={handleEditField} darkMode={darkMode} />;
      case 'admin':
      case 'super-admin': return <AdminPanel userData={userData} darkMode={darkMode} />;
      default: return <ParticulierPanel userData={userData} editCount={editCount} MAX_EDITS={MAX_EDITS} onEdit={handleEditField} darkMode={darkMode} />;
    }
  };

  return (
    <div className={`min-h-screen font-sans ${darkMode ? 'bg-[#020617] text-zinc-400' : 'bg-[#f4f7f9] text-zinc-600'}`}>

      {/* ── HEADER ── */}
      <header className={`h-14 flex items-center justify-between px-6 border-b sticky top-0 z-40 backdrop-blur-xl ${darkMode ? 'border-white/5 bg-black/60' : 'border-zinc-200 bg-white/80'}`}>
        <button
          onClick={() => navigate('/dashboard')}
          className={`flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest transition-colors ${darkMode ? 'text-zinc-500 hover:text-orange-400' : 'text-zinc-500 hover:text-orange-600'}`}
        >
          <ArrowLeft size={14} />
          <span>Back_to_Dashboard</span>
        </button>

        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-1 border text-[9px] font-mono uppercase ${roleConfig.color} ${roleConfig.border} ${darkMode ? 'bg-black/40' : 'bg-white'}`}>
            {roleConfig.icon}
            <span>{roleConfig.label}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse ml-1" />
          </div>
          
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-6 pb-24">

        {/* ── HERO CARD ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className={`relative overflow-hidden border ${darkMode ? 'border-white/5 bg-black/30' : 'border-zinc-200 bg-white shadow-sm'}`}
        >
          <ScanLine />
          <GridPattern />

          {/* accent strip */}
          <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: roleConfig.accent, boxShadow: `0 0 20px ${roleConfig.accent}` }} />

          <div className="relative z-10 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            <Avatar userData={userData} onUpload={handleUploadPhoto} roleConfig={roleConfig} darkMode={darkMode} />

            <div className="flex-1 space-y-3">
              <div>
                <div className="text-[8px] font-mono text-zinc-600 uppercase tracking-[0.4em] mb-1">
                  Node_Identity // {`ID_${userData.id?.slice(-8).toUpperCase() || 'UNKNOWN'}`}
                </div>
                <h1 className={`text-3xl md:text-4xl font-black uppercase tracking-tighter leading-none ${darkMode ? 'text-white' : 'text-zinc-900'}`}>
                  {userData.nom}
                  <span style={{ color: roleConfig.accent }}> {userData.prenom}</span>
                </h1>
                {isOrg && userData.organizationName && (
                  <div className={`text-[11px] font-mono mt-1 ${roleConfig.color}`}>{userData.organizationName}</div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {userData.email && (
                  <div className={`flex items-center gap-1.5 text-[10px] font-mono ${darkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
                    <Mail size={10} />
                    {userData.email}
                  </div>
                )}
                {userData.phoneNumber && (
                  <div className={`flex items-center gap-1.5 text-[10px] font-mono ${darkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
                    <Phone size={10} />
                    {userData.phoneNumber}
                  </div>
                )}
                <div className={`flex items-center gap-1.5 text-[10px] font-mono ${darkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>
                  <CheckCircle2 size={10} className="text-emerald-500" />
                  Inscrit le {formatDate(userData.createdAt)}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className={`px-2.5 py-1 text-[9px] font-mono font-bold uppercase border ${roleConfig.color} ${roleConfig.border} ${darkMode ? 'bg-black/40' : 'bg-white'}`}>
                  {getRank(userData.scoreConfiance)}
                </span>
                <span className={`px-2.5 py-1 text-[9px] font-mono font-bold uppercase border ${userData.isPremium ? 'text-yellow-400 border-yellow-500/30 bg-yellow-500/5' : `${darkMode ? 'text-zinc-600 border-white/5' : 'text-zinc-400 border-zinc-200'}`}`}>
                  {userData.subscription?.plan?.toUpperCase() || 'FREEMIUM'}
                </span>
                <span className={`px-2.5 py-1 text-[9px] font-mono uppercase border ${userData.status === 'active' ? 'text-emerald-400 border-emerald-500/30' : 'text-red-400 border-red-500/30'}`}>
                  {userData.status?.toUpperCase() || 'ACTIVE'}
                </span>
              </div>
            </div>

            {/* Right: progress ring */}
            <div className="hidden md:block">
              <ProgressRing
                value={userData.pesas}
                max={500}
                accent={roleConfig.accent}
                label="PESA"
                sub={`Objectif: ${userData.pesas < 300 ? 300 : 500}`}
                darkMode={darkMode}
              />
            </div>
          </div>
        </motion.div>

        {/* ── STATS ROW ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className={`grid grid-cols-2 md:grid-cols-4 border divide-x ${darkMode ? 'border-white/5 divide-white/5' : 'border-zinc-200 divide-zinc-200'}`}
        >
          <StatChip label="Pesa_Reserve" value={userData.pesas} color="orange" darkMode={darkMode} />
          <StatChip label="Score_Confiance" value={`${userData.scoreConfiance}%`} color="emerald" darkMode={darkMode} />
          <StatChip label="Modifications" value={`${editCount}/${MAX_EDITS}`} color="sky" darkMode={darkMode} />
          <StatChip label="Abonnement" value={(userData.subscription?.plan || 'FREE').toUpperCase()} color="purple" darkMode={darkMode} />
        </motion.div>

        {/* ── MAIN GRID ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Role-specific data — 2 cols */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`relative overflow-hidden col-span-2 border ${darkMode ? 'border-white/5 bg-black/20' : 'border-zinc-200 bg-white shadow-sm'}`}
          >
            <GridPattern />
            <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: roleConfig.accent, opacity: 0.4 }} />
            <div className="relative z-10 p-6">
              <SectionHeader
                icon={<Database size={14} />}
                label="Données_Profil"
                sub={roleConfig.label}
                darkMode={darkMode}
              />
              {renderRolePanel()}
            </div>
          </motion.section>

          {/* Right col — contact + progress */}
          <div className="space-y-5">
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={`relative overflow-hidden border ${darkMode ? 'border-white/5 bg-black/20' : 'border-zinc-200 bg-white shadow-sm'}`}
            >
              <GridPattern />
              <div className="relative z-10 p-5">
                <SectionHeader icon={<User size={14} />} label="Contact_Info" darkMode={darkMode} />
                <div className="space-y-3">
                  <DataRow icon={<Mail size={12} />} label="Email" value={userData.email} darkMode={darkMode} />
                  <DataRow
                    icon={<Phone size={12} />}
                    label="Téléphone"
                    value={userData.phoneNumber}
                    editable={editCount < MAX_EDITS}
                    onEdit={() => handleEditField('phoneNumber')}
                    darkMode={darkMode}
                  />
                  <DataRow icon={<Globe size={12} />} label="Langue" value={userData.language?.toUpperCase()} darkMode={darkMode} />
                </div>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className={`relative overflow-hidden border ${darkMode ? 'border-white/5 bg-black/20' : 'border-zinc-200 bg-white shadow-sm'}`}
            >
              <GridPattern />
              <div className="relative z-10 p-5">
                <SectionHeader icon={<Activity size={14} />} label="Progression_PESA" darkMode={darkMode} />

                <div className="space-y-3">
                  {/* mini progress bar */}
                  <div>
                    <div className="flex justify-between text-[8px] font-mono text-zinc-600 uppercase mb-1.5">
                      <span>Current_Load</span>
                      <span style={{ color: roleConfig.accent }}>{pct.toFixed(0)}%</span>
                    </div>
                    <div className={`h-1.5 w-full overflow-hidden ${darkMode ? 'bg-white/5' : 'bg-zinc-100'}`}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        className="h-full"
                        style={{ background: roleConfig.accent, boxShadow: `0 0 8px ${roleConfig.accent}80` }}
                      />
                    </div>
                  </div>

                  {[
                    { label: 'Débutant', threshold: 0, max: 220 },
                    { label: 'Intermédiaire', threshold: 220, max: 300 },
                    { label: 'Avancé', threshold: 300, max: 500 },
                    { label: 'Expert', threshold: 500, max: 500 },
                  ].map((tier) => {
                    const reached = userData.pesas >= tier.threshold;
                    return (
                      <div key={tier.label} className={`flex items-center gap-2 py-1 border-b ${darkMode ? 'border-white/5' : 'border-zinc-100'}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${reached ? '' : 'bg-zinc-700'}`}
                          style={reached ? { background: roleConfig.accent, boxShadow: `0 0 6px ${roleConfig.accent}` } : {}}
                        />
                        <span className={`text-[9px] font-mono uppercase ${reached ? (darkMode ? 'text-zinc-300' : 'text-zinc-700') : 'text-zinc-600'}`}>
                          {tier.label}
                        </span>
                        <span className="ml-auto text-[8px] font-mono text-zinc-700">{tier.max} PESA</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.section>
          </div>
        </div>

        {/* ── CONNECTIONS ── */}
        <ConnectionsSection connexions={userData.connexions} darkMode={darkMode} />

        {/* ── EDIT QUOTA WARNING ── */}
        {editCount >= MAX_EDITS && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`flex items-center gap-3 p-4 border text-[10px] font-mono uppercase ${darkMode ? 'border-yellow-500/20 bg-yellow-500/5 text-yellow-400' : 'border-yellow-400/30 bg-yellow-50 text-yellow-600'}`}
          >
            <AlertTriangle size={14} />
            <span>Quota_Modif_Atteint — Réinitialisé demain à 00:00</span>
          </motion.div>
        )}

      </div>

      {/* ── BOTTOM ACTION BAR ── */}
      <ActionBar onEdit={handleEditField} darkMode={darkMode} navigate={navigate} />
    </div>
  );
};

export default UserProfile;